import { useEffect, useId, useRef, type CSSProperties } from 'react';
import { observer } from 'mobx-react-lite';
import type { PendingConsentStore } from '~/stores';
import { Button } from '~/ui/primitives';
import { LogoMark } from '~/ui/Logo';
import { capabilityDetail, capabilityLabel, formatDuration, isKnownCapability, truncateMiddle } from '~/ui/format';

/** Render a host origin as a compact, readable chip (scheme + host). */
function OriginChip({ origin }: { origin: string }) {
  let display = origin;
  try {
    display = new URL(origin).host || origin;
  } catch {
    /* keep raw */
  }
  return (
    <span style={originChipStyle} title={origin}>
      {display}
    </span>
  );
}

export interface ConsentPromptProps {
  store: PendingConsentStore;
}

/**
 * Popup-side modal shown when the host page calls `requestDelegation` and no
 * matching auto-approve rule exists. Spec §3.
 *
 * The store holds at most one pending consent; clicking a button calls
 * `store.resolveCurrent()`, which resolves the handler's awaiting promise.
 */
export const ConsentPrompt = observer(({ store }: ConsentPromptProps) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const denyRef = useRef<HTMLButtonElement | null>(null);
  const onceRef = useRef<HTMLButtonElement | null>(null);
  const alwaysRef = useRef<HTMLButtonElement | null>(null);
  const p = store.pending;

  useEffect(() => {
    if (!p) return;
    denyRef.current?.focus();
  }, [p]);

  useEffect(() => {
    if (!p) return;
    const node = dialogRef.current;
    if (!node) return;

    const onKey = (e: KeyboardEvent) => {
      // Ignore modifier combos and IME composition — let the browser handle them.
      if (e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        store.resolveCurrent({ kind: 'deny' });
        return;
      }
      if (e.key !== 'Tab') return;

      // Only trap when focus is inside this dialog — never hijack the rest of the page.
      if (!node.contains(document.activeElement)) return;

      // The sign branch renders only Deny + Approve (no "Always approve"), so
      // alwaysRef.current stays null there; filter(Boolean) drops it and the
      // trap cycles the two rendered buttons. The delegation branch renders all
      // three. Keep this filter if you add/remove buttons per branch.
      const order = [denyRef.current, onceRef.current, alwaysRef.current].filter(Boolean) as HTMLElement[];
      if (order.length === 0) return;
      const idx = order.indexOf(document.activeElement as HTMLElement);
      if (idx === -1) {
        e.preventDefault();
        order[0].focus();
        return;
      }
      const next = e.shiftKey
        ? idx === 0
          ? order[order.length - 1]
          : order[idx - 1]
        : idx === order.length - 1
        ? order[0]
        : order[idx + 1];
      e.preventDefault();
      next.focus();
    };

    node.addEventListener('keydown', onKey);
    return () => node?.removeEventListener('keydown', onKey);
  }, [p, store]);

  if (!p) return null;
  const req = p.request;
  return (
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} style={modalStyle}>
      <div style={cardStyle}>
        <div style={brandRowStyle}>
          <LogoMark size={22} />
          <span
            style={{
              font: '700 11px/1 var(--font-sans)',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--text-secondary)',
            }}
          >
            SCP Wallet
          </span>
        </div>
        {req.kind === 'sign' ? (
          <>
            <h2 id={titleId} style={titleStyle}>
              Authorize a blockchain transaction
            </h2>
            <p style={leadStyle}>
              <OriginChip origin={req.hostOrigin} /> wants you to sign
              {req.signKind === 'extrinsic' ? ' a blockchain transaction' : ` (${req.signKind})`}.
            </p>
            <div style={panelStyle}>
              <div style={panelLabelStyle}>What you're signing</div>
              <div style={{ color: 'var(--text-primary)' }}>
                {req.humanReadable ? req.humanReadable : 'No description was provided by the app.'}
              </div>
              <div style={{ marginTop: 'var(--space-3)' }}>
                <span style={panelLabelStyle}>Payload digest</span>
                <code style={digestStyle}>{truncateMiddle(req.payloadDigest, 10, 10)}</code>
              </div>
            </div>
            <div style={buttonColStyle}>
              <Button
                ref={onceRef}
                fullWidth
                onClick={() => store.resolveCurrent({ kind: 'approve-once' })}
                aria-label="Approve and sign"
              >
                Approve &amp; sign
              </Button>
              <Button
                ref={denyRef}
                variant="tertiary"
                fullWidth
                onClick={() => store.resolveCurrent({ kind: 'deny' })}
                aria-label="Deny signing request"
              >
                Deny
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2 id={titleId} style={titleStyle}>
              App is requesting permission
            </h2>
            <p style={leadStyle}>
              <OriginChip origin={req.hostOrigin} /> wants to:
            </p>
            <ul style={capListStyle}>
              {req.capabilities.map((c) => (
                <li key={c} style={capItemStyle}>
                  <span style={capCheckStyle} aria-hidden>
                    ✓
                  </span>
                  <span>
                    <span style={{ font: '500 15px/1.3 var(--font-sans)', color: 'var(--text-primary)' }}>
                      {capabilityLabel(c)}
                    </span>
                    {capabilityDetail(c) && <span style={capDetailStyle}>{capabilityDetail(c)}</span>}
                    {!isKnownCapability(c) && <span style={capUnknownStyle}>Unrecognized permission: {c}</span>}
                  </span>
                </li>
              ))}
            </ul>
            <p style={ttlStyle}>Access lasts up to {formatDuration(req.ttlSeconds)}.</p>
            <div style={buttonColStyle}>
              <Button
                ref={onceRef}
                fullWidth
                onClick={() => store.resolveCurrent({ kind: 'approve-once' })}
                aria-label="Approve once"
              >
                Approve once
              </Button>
              <Button
                ref={alwaysRef}
                variant="secondary"
                fullWidth
                onClick={() => store.resolveCurrent({ kind: 'approve-always' })}
                aria-label="Always approve"
              >
                Always approve for this app
              </Button>
              <Button
                ref={denyRef}
                variant="tertiary"
                fullWidth
                onClick={() => store.resolveCurrent({ kind: 'deny' })}
                aria-label="Deny delegation request"
              >
                Deny
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

const modalStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(29,27,32,0.55)',
  backdropFilter: 'blur(2px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--space-5)',
  fontFamily: 'var(--font-sans)',
  zIndex: 1000,
};

const cardStyle: CSSProperties = {
  background: 'var(--surface-contrast)',
  borderRadius: 'var(--radius-xl)',
  padding: 'var(--space-6)',
  maxWidth: 420,
  width: '100%',
  boxShadow: 'var(--shadow-xl)',
  border: '1px solid var(--stroke-on-low)',
};

const brandRowStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--space-4)' };
const titleStyle: CSSProperties = {
  margin: '0 0 var(--space-3)',
  font: '600 22px/1.25 var(--font-display)',
  letterSpacing: '-0.01em',
  color: 'var(--text-primary)',
};
const leadStyle: CSSProperties = {
  margin: '0 0 var(--space-4)',
  color: 'var(--text-secondary)',
  font: '400 15px/1.5 var(--font-sans)',
};
const originChipStyle: CSSProperties = {
  display: 'inline-block',
  font: '500 13px/1 var(--font-mono)',
  color: 'var(--c-violet-dark)',
  background: 'var(--c-violet-light)',
  borderRadius: 'var(--radius-sm)',
  padding: '3px 8px',
};
const capListStyle: CSSProperties = {
  listStyle: 'none',
  margin: '0 0 var(--space-4)',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};
const capItemStyle: CSSProperties = { display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' };
const capCheckStyle: CSSProperties = {
  flex: '0 0 auto',
  width: 22,
  height: 22,
  borderRadius: '50%',
  background: 'var(--c-green-light)',
  color: 'var(--c-green-dark)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  font: '600 12px/1 var(--font-sans)',
};
const capDetailStyle: CSSProperties = {
  display: 'block',
  font: '400 13px/1.4 var(--font-sans)',
  color: 'var(--text-secondary)',
  marginTop: 2,
};
const capUnknownStyle: CSSProperties = {
  display: 'block',
  font: '400 12px/1.4 var(--font-sans)',
  color: 'var(--c-red)',
  marginTop: 2,
};
const ttlStyle: CSSProperties = {
  margin: '0 0 var(--space-5)',
  color: 'var(--text-secondary)',
  font: '400 13px/1.5 var(--font-sans)',
};
const panelStyle: CSSProperties = {
  background: 'var(--surface-low)',
  border: '1px solid var(--stroke-on-low)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-4)',
  marginBottom: 'var(--space-5)',
  font: '400 14px/1.5 var(--font-sans)',
};
const panelLabelStyle: CSSProperties = {
  display: 'block',
  font: '700 10px/1 var(--font-sans)',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  marginBottom: 4,
};
const digestStyle: CSSProperties = {
  font: '400 13px/1.4 var(--font-mono)',
  color: 'var(--text-secondary)',
  wordBreak: 'break-all',
};
const buttonColStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
  marginTop: 'var(--space-4)',
};
